import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { DashboardService } from '../services/dashboardService';
import { DashboardLayout, LayoutSlot, SlotAssignment } from '../types/Layout';
import { SUGGESTED_LAYOUTS, isSuggestedLayoutId } from '../data/suggestedLayouts';
import { useToast } from '@/shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useViewScope } from '@/shared/hooks/use-view-scope';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';

const PREFERENCES_KEY_BASE = 'dashboard-preferences';

export interface UseDashboardLayoutsResult {
  userLayouts: DashboardLayout[];
  suggestedLayouts: DashboardLayout[];
  availableLayouts: DashboardLayout[];
  activeLayout: DashboardLayout | undefined;
  currentLayoutId: string | undefined;
  isLoading: boolean;
  setActiveLayout: (layoutId: string | null) => void;
  createLayout: (name: string, slots?: LayoutSlot[], assignments?: SlotAssignment[]) => Promise<DashboardLayout | undefined>;
  duplicateLayout: (sourceId: string, newName?: string) => Promise<DashboardLayout | undefined>;
  renameLayout: (id: string, newName: string) => Promise<void>;
  deleteLayout: (id: string) => Promise<void>;
  updateLayoutSlots: (id: string, slots: LayoutSlot[], assignments?: SlotAssignment[]) => Promise<void>;
  assignDashboardToSlot: (layoutId: string, slotId: string, dashboardUserId: string) => Promise<void>;
  unassignSlot: (layoutId: string, slotId: string) => Promise<void>;
  addSlot: (layoutId: string, slot: Omit<LayoutSlot, 'id'> & { id?: string }) => Promise<void>;
  removeSlot: (layoutId: string, slotId: string) => Promise<void>;
}

export const useDashboardLayouts = (): UseDashboardLayoutsResult => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  // PBI #78540: scope context drives both the READ (cascade resolver) and
  // the WRITE (byScope slot in user_settings). Without it, the GET falls back
  // to the legacy flat user_settings row and ignores org/product/platform
  // defaults — which means a user with empty user_settings sees nothing even
  // when a superadmin set a default layout.
  //
  // CRITICAL: orgId must come from `selectedOrganization` (superadmin's
  // current viewing org) before the JWT session org. Otherwise a superadmin
  // viewing Org B's dashboards would resolve against their primary org's
  // prefs from session — wrong cascade, stale data across nav.
  const { scope: viewScope } = useViewScope();
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const effectiveOrgId = selectedOrganization?.id ?? user?.organizationId ?? undefined;
  const saveContext = {
    scope: viewScope,
    workspaceId: viewScope === 'product' ? currentWorkspace?.id : undefined,
    orgId: viewScope === 'organization' || viewScope === 'product' ? effectiveOrgId : undefined,
  };
  // Cache key includes orgId so cross-org navigation doesn't reuse stale prefs.
  const PREFERENCES_KEY = [
    PREFERENCES_KEY_BASE,
    viewScope,
    saveContext.orgId ?? null,
    saveContext.workspaceId ?? null,
  ];

  const { data: preferences, isLoading } = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => {
      const backendScope =
        viewScope === 'platform' ? 'superadmin' : viewScope;
      if (backendScope) {
        const result = await DashboardService.getUserPreferencesWithSource({
          scope: backendScope,
          orgId: saveContext.orgId,
          productId: saveContext.workspaceId,
        });
        return result.data;
      }
      return DashboardService.getUserPreferences();
    },
    staleTime: 5 * 60 * 1000,
  });

  const userLayouts = useMemo<DashboardLayout[]>(() => preferences?.layouts ?? [], [preferences]);
  const currentLayoutId = preferences?.currentLayoutId;

  const availableLayouts = useMemo<DashboardLayout[]>(
    () => [...SUGGESTED_LAYOUTS, ...userLayouts],
    [userLayouts]
  );

  const activeLayout = useMemo<DashboardLayout | undefined>(() => {
    if (!currentLayoutId) return undefined;
    return availableLayouts.find((l) => l.id === currentLayoutId);
  }, [availableLayouts, currentLayoutId]);

  const savePreferencesMutation = useMutation({
    mutationFn: (patch: { layouts?: DashboardLayout[]; currentLayoutId?: string | null }) =>
      DashboardService.saveUserPreferences(patch, saveContext),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_KEY });
      const previous = queryClient.getQueryData<typeof preferences>(PREFERENCES_KEY);
      queryClient.setQueryData<typeof preferences>(PREFERENCES_KEY, (old) => {
        if (!old) return old;
        const next = { ...old };
        if (patch.layouts !== undefined) next.layouts = patch.layouts;
        if (patch.currentLayoutId !== undefined) next.currentLayoutId = patch.currentLayoutId ?? undefined;
        // Mirror the patch into the active tab's slice as well, otherwise the
        // edit only lives at the root level. Switching away and back would
        // re-mirror tabs[active] (stale) over the root, throwing away the
        // pending change. Backend's merge already routes root-level patches
        // into the active tab on the wire, so we just need to keep our
        // local cache aligned.
        if (next.tabs && next.tabs.length > 0 && next.currentTabId) {
          const activeId = next.currentTabId;
          next.tabs = next.tabs.map((tab) => {
            if (tab.id !== activeId) return tab;
            return {
              ...tab,
              ...(patch.layouts !== undefined ? { layouts: patch.layouts } : {}),
              ...(patch.currentLayoutId !== undefined ? { currentLayoutId: patch.currentLayoutId ?? undefined } : {}),
              updatedAt: new Date().toISOString(),
            };
          });
        }
        return next;
      });
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PREFERENCES_KEY, context.previous);
      }
      toast({
        title: t('dashboards.layouts.saveError', 'Erro ao salvar layout'),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      // PBI #78540: garante que a próxima rename/delete/duplicate de tab leia
      // tabs[] com layouts + assignments atualizados (o backend espelha o
      // patch em tabs[active]; sem invalidar, useDashboardTabs vê stale).
      queryClient.invalidateQueries({ queryKey: ['dashboard-preferences'] });
    },
  });

  // One-shot migration: tile size mínimo virou w=2,h=2 (cabem 6 numa
  // linha). Layouts custom criados antes têm tiles maiores (w=3+, h=3+).
  // Clampamos uma vez por browser. Após salvar, flag em localStorage
  // evita re-disparo (o user pode redimensionar maior depois sem reset).
  const migrationRanRef = useRef(false);
  useEffect(() => {
    if (migrationRanRef.current) return;
    if (!userLayouts || userLayouts.length === 0) return;
    const FLAG = 'prodgy.dashboard-tile-size-migrated-v2';
    try {
      if (localStorage.getItem(FLAG) === '1') {
        migrationRanRef.current = true;
        return;
      }
    } catch { /* localStorage indisponível */ }

    const needsFix = userLayouts.some((l) =>
      l.slots.some((s) => s.formFactor === 'tile' && (s.w > 2 || s.h > 2))
    );
    if (!needsFix) {
      migrationRanRef.current = true;
      try { localStorage.setItem(FLAG, '1'); } catch { /* noop */ }
      return;
    }

    const normalized = userLayouts.map((l) => ({
      ...l,
      slots: l.slots.map((s) =>
        s.formFactor === 'tile' && (s.w > 2 || s.h > 2)
          ? { ...s, w: 2, h: 2, size: 'small' as const }
          : s
      ),
    }));
    migrationRanRef.current = true;
    savePreferencesMutation.mutate({ layouts: normalized });
    try { localStorage.setItem(FLAG, '1'); } catch { /* noop */ }
  }, [userLayouts, savePreferencesMutation]);

  const setActiveLayout = useCallback(
    (layoutId: string | null) => {
      savePreferencesMutation.mutate({ currentLayoutId: layoutId });
    },
    [savePreferencesMutation]
  );

  const createLayout = useCallback(
    async (name: string, slots: LayoutSlot[] = [], assignments: SlotAssignment[] = []): Promise<DashboardLayout | undefined> => {
      const now = new Date().toISOString();
      const newLayout: DashboardLayout = {
        id: uuidv4(),
        name,
        slots,
        assignments,
        isSuggested: false,
        createdAt: now,
        updatedAt: now,
      };
      const nextLayouts = [...userLayouts, newLayout];
      await savePreferencesMutation.mutateAsync({ layouts: nextLayouts, currentLayoutId: newLayout.id });
      return newLayout;
    },
    [userLayouts, savePreferencesMutation]
  );

  const duplicateLayout = useCallback(
    async (sourceId: string, newName?: string): Promise<DashboardLayout | undefined> => {
      const source = availableLayouts.find((l) => l.id === sourceId);
      if (!source) return undefined;
      const now = new Date().toISOString();
      const cloned: DashboardLayout = {
        id: uuidv4(),
        name: newName || `${source.name} ${t('dashboards.layouts.copySuffix', '(cópia)')}`,
        slots: source.slots.map((s) => ({ ...s })),
        assignments: source.assignments.map((a) => ({ ...a })),
        isSuggested: false,
        createdAt: now,
        updatedAt: now,
      };
      const nextLayouts = [...userLayouts, cloned];
      await savePreferencesMutation.mutateAsync({ layouts: nextLayouts, currentLayoutId: cloned.id });
      return cloned;
    },
    [availableLayouts, userLayouts, savePreferencesMutation, t]
  );

  const renameLayout = useCallback(
    async (id: string, newName: string): Promise<void> => {
      if (isSuggestedLayoutId(id)) return;
      const nextLayouts = userLayouts.map((l) =>
        l.id === id ? { ...l, name: newName, updatedAt: new Date().toISOString() } : l
      );
      await savePreferencesMutation.mutateAsync({ layouts: nextLayouts });
    },
    [userLayouts, savePreferencesMutation]
  );

  const deleteLayout = useCallback(
    async (id: string): Promise<void> => {
      if (isSuggestedLayoutId(id)) return;
      const nextLayouts = userLayouts.filter((l) => l.id !== id);
      const nextCurrent = currentLayoutId === id ? null : currentLayoutId;
      await savePreferencesMutation.mutateAsync({
        layouts: nextLayouts,
        currentLayoutId: nextCurrent ?? null,
      });
    },
    [userLayouts, currentLayoutId, savePreferencesMutation]
  );

  const updateLayoutSlots = useCallback(
    async (id: string, slots: LayoutSlot[], assignments?: SlotAssignment[]): Promise<void> => {
      if (isSuggestedLayoutId(id)) {
        toast({
          title: t('dashboards.layouts.suggestedReadOnly', 'Layout sugerido não pode ser editado. Duplique para personalizar.'),
        });
        return;
      }
      const nextLayouts = userLayouts.map((l) =>
        l.id === id
          ? {
              ...l,
              slots,
              assignments: assignments ?? l.assignments,
              updatedAt: new Date().toISOString(),
            }
          : l
      );
      await savePreferencesMutation.mutateAsync({ layouts: nextLayouts });
    },
    [userLayouts, savePreferencesMutation, toast, t]
  );

  const findUserLayout = useCallback(
    (layoutId: string) => userLayouts.find((l) => l.id === layoutId),
    [userLayouts]
  );

  const assignDashboardToSlot = useCallback(
    async (layoutId: string, slotId: string, dashboardUserId: string): Promise<void> => {
      if (isSuggestedLayoutId(layoutId)) {
        toast({
          title: t('dashboards.layouts.suggestedReadOnly', 'Layout sugerido não pode ser editado. Duplique para personalizar.'),
        });
        return;
      }
      const layout = findUserLayout(layoutId);
      if (!layout) return;
      const filtered = layout.assignments.filter(
        (a) => a.slotId !== slotId && a.userDashboardId !== dashboardUserId
      );
      const nextAssignments = [...filtered, { slotId, userDashboardId: dashboardUserId }];
      await updateLayoutSlots(layoutId, layout.slots, nextAssignments);
    },
    [findUserLayout, updateLayoutSlots, toast, t]
  );

  const unassignSlot = useCallback(
    async (layoutId: string, slotId: string): Promise<void> => {
      if (isSuggestedLayoutId(layoutId)) {
        toast({
          title: t('dashboards.layouts.suggestedReadOnly', 'Layout sugerido não pode ser editado. Duplique para personalizar.'),
        });
        return;
      }
      const layout = findUserLayout(layoutId);
      if (!layout) return;
      const nextAssignments = layout.assignments.filter((a) => a.slotId !== slotId);
      await updateLayoutSlots(layoutId, layout.slots, nextAssignments);
    },
    [findUserLayout, updateLayoutSlots, toast, t]
  );

  const addSlot = useCallback(
    async (layoutId: string, slot: Omit<LayoutSlot, 'id'> & { id?: string }): Promise<void> => {
      if (isSuggestedLayoutId(layoutId)) return;
      const layout = findUserLayout(layoutId);
      if (!layout) return;
      const newSlot: LayoutSlot = { ...slot, id: slot.id ?? uuidv4() };
      await updateLayoutSlots(layoutId, [...layout.slots, newSlot]);
    },
    [findUserLayout, updateLayoutSlots]
  );

  const removeSlot = useCallback(
    async (layoutId: string, slotId: string): Promise<void> => {
      if (isSuggestedLayoutId(layoutId)) {
        toast({
          title: t('dashboards.layouts.suggestedReadOnly', 'Layout sugerido não pode ser editado. Duplique para personalizar.'),
        });
        return;
      }
      const layout = findUserLayout(layoutId);
      if (!layout) return;
      const nextSlots = layout.slots.filter((s) => s.id !== slotId);
      const nextAssignments = layout.assignments.filter((a) => a.slotId !== slotId);
      await updateLayoutSlots(layoutId, nextSlots, nextAssignments);
    },
    [findUserLayout, updateLayoutSlots, toast, t]
  );

  return {
    userLayouts,
    suggestedLayouts: SUGGESTED_LAYOUTS,
    availableLayouts,
    activeLayout,
    currentLayoutId,
    isLoading,
    setActiveLayout,
    createLayout,
    duplicateLayout,
    renameLayout,
    deleteLayout,
    updateLayoutSlots,
    assignDashboardToSlot,
    unassignSlot,
    addSlot,
    removeSlot,
  };
};
